import { Form, Input, Switch } from 'antd';
import { useState } from 'react';

import { apiFetch } from '@/api/client';
import { branchesKey, useBranches, type Branch } from '@/api/masters';
import { submitVia, useApiMutation } from '@/api/mutations';
import { ApiErrorAlert } from '@/components/ApiErrorAlert';
import { ConfirmButton } from '@/components/ConfirmButton';
import { DataView, type DataViewColumn } from '@/components/DataView';
import { FormDrawer } from '@/components/FormDrawer';
import { IconAction } from '@/components/IconAction';
import { Button, Card, EmptyState, PageHeading } from '@/components/PageHeading';
import { StatusBadge } from '@/components/StatusBadge';
import { RecordAction, WriteAction } from '@/components/WriteAction';
import { blankToNull } from '@core/format/text';
import type { Session } from '@core/session/types';

type Editing = { mode: 'create' } | { mode: 'edit'; branch: Branch };

/**
 * Branch master (spec Section 11).
 *
 * The reference list screen, and the one the other eight copy: heading and toolbar on the canvas,
 * a single card holding the table, and every write routed through `WriteAction` so the UI never
 * offers a button the API is going to refuse.
 *
 * Ant Design's `Table` does the grid — the template reaches for the same component for its own
 * datatable, so this is following it rather than departing from it. `ProTable` is gone, and with
 * it its toolbar: column settings and density were configuration nobody had asked for on a list
 * of at most a few dozen branches, and they cost the entire ProComponents bundle.
 */
export function BranchesPage({ session }: { session: Session }) {
  const [includeInactive, setIncludeInactive] = useState(false);
  const [editing, setEditing] = useState<Editing | null>(null);

  const branches = useBranches(includeInactive);

  const remove = useApiMutation<void, Branch>({
    mutationFn: (branch) => apiFetch<void>(`/api/branch/${branch.id}`, { method: 'DELETE' }),
    success: (_result, branch) => `${branch.name} deleted.`,
    invalidates: [branchesKey],
  });

  const columns: DataViewColumn<Branch>[] = [
    {
      title: 'Code',
      dataIndex: 'code',
      width: 110,
      render: (_, row) => <span className="font-mono-nums font-semibold">{row.code}</span>,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      // The card heading: the name and whether it is head office is how a branch is identified.
      primary: true,
      render: (_, row) => (
        <span className="inline-flex items-center gap-2">
          <span className="font-medium text-gray-900">{row.name}</span>
          {row.isHeadOffice && <StatusBadge tone="brand">Head office</StatusBadge>}
        </span>
      ),
    },
    {
      title: 'GSTIN',
      dataIndex: 'gstin',
      width: 170,
      responsive: ['xl'],
      // Off the card, along with Contact below. Both are already `responsive: ['xl']` — the page
      // author's own judgement that they are the first two to go when width is short — and a card
      // is the shortest width there is.
      hideOnCard: true,
      // Per branch, not per company: GST registration is per state, and this is what decides
      // CGST/SGST against IGST once the GST module activates (spec Section 19).
      render: (_, row) => (row.gstin ? <span className="font-mono-nums">{row.gstin}</span> : '—'),
    },
    { title: 'City', dataIndex: 'city', width: 140, responsive: ['md'] },
    { title: 'State', dataIndex: 'stateName', width: 140, responsive: ['lg'] },
    {
      title: 'PIN',
      dataIndex: 'pincode',
      width: 100,
      responsive: ['lg'],
      // The mono face is for digits that need to line up; a missing value falls back to an em dash
      // in the body font, so it reads as absent rather than as a very short number.
      render: (_, row) =>
        row.pincode ? <span className="font-mono-nums">{row.pincode}</span> : '—',
    },
    {
      title: 'Contact',
      dataIndex: 'contactPerson',
      width: 160,
      responsive: ['xl'],
      hideOnCard: true,
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      width: 96,
      render: (_, row) =>
        row.isActive ? (
          <StatusBadge tone="positive">Active</StatusBadge>
        ) : (
          <StatusBadge tone="neutral">Inactive</StatusBadge>
        ),
    },
    {
      title: '',
      key: 'actions',
      width: 96,
      align: 'right',
      fixed: 'right',
      trailing: true,
      render: (_, row) => (
        <div className="flex justify-end gap-1">
          <WriteAction session={session} required="Branch:Edit">
            {(disabled) => (
              <IconAction
                icon="icon icon-pencil-line"
                label={`Edit ${row.name}`}
                disabled={disabled}
                onClick={() => setEditing({ mode: 'edit', branch: row })}
              />
            )}
          </WriteAction>

          <RecordAction
            session={session}
            required="Branch:Delete"
            // The head office is what the consolidated all-branch view is anchored to, and the
            // server refuses to delete it. Saying so here beats letting them press it and read
            // the refusal afterwards.
            forbidden={row.isHeadOffice ? 'The head office cannot be deleted' : undefined}
          >
            {(disabled) => (
              <ConfirmButton
                icon="icon icon-trash-2"
                label={`Delete ${row.name}`}
                title="Delete this branch?"
                description="It is retained for audit, not erased, but it stops appearing anywhere."
                confirmText="Delete"
                disabled={disabled}
                pending={remove.isPending}
                onConfirm={() => void remove.mutateAsync(row).catch(() => undefined)}
              />
            )}
          </RecordAction>
        </div>
      ),
    },
  ];

  if (branches.isError) {
    return <ApiErrorAlert error={branches.error} />;
  }

  const rows = branches.data ?? [];

  return (
    <>
      <PageHeading
        title="Branches"
        subtitle="Units and offices that transactions are booked against."
        actions={
          <>
            <Button
              icon={includeInactive ? 'icon icon-eye-off' : 'icon icon-eye'}
              onClick={() => setIncludeInactive((on) => !on)}
            >
              {includeInactive ? 'Hide inactive' : 'Show inactive'}
            </Button>

            <WriteAction session={session} required="Branch:Create">
              {(disabled) => (
                <Button
                  variant="primary"
                  icon="icon icon-plus"
                  shortcut="create"
                  disabled={disabled}
                  onClick={() => setEditing({ mode: 'create' })}
                >
                  New branch
                </Button>
              )}
            </WriteAction>
          </>
        }
      />

      <Card flush className="p-4 pb-0 max-sm:p-3 max-sm:pb-1">
        {/* `DataView` keeps the table's `scroll={{ x: 'max-content' }}` above 640px — without it
            the fixed-width columns run off the right of the page, taking the card's border with
            them — and drops to stacked cards below, where scrolling sideways is not an answer. */}
        <DataView<Branch>
          columns={columns}
          dataSource={rows}
          loading={branches.isLoading}
          empty={
            <EmptyState
              icon="icon icon-building-2"
              title="No branches yet"
              hint="Every document is booked against a branch, so this is the first thing to set up."
            />
          }
        />
      </Card>

      {editing && <BranchDrawer editing={editing} onClose={() => setEditing(null)} />}
    </>
  );
}

interface BranchFormValues {
  code: string;
  name: string;
  isHeadOffice: boolean;
  gstin?: string | null;
  stateCode?: string | null;
  stateName?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  pincode?: string | null;
  contactPerson?: string | null;
  contactPhone?: string | null;
  contactEmail?: string | null;
  isActive: boolean;
}

/**
 * Create and edit in one drawer.
 *
 * `code` is disabled on edit rather than hidden, because it is the branch's identity everywhere
 * else — it is the branch segment of every document number already issued, so changing it would
 * rewrite history. The server agrees: `UpdateBranchRequest` has no code field at all.
 */
function BranchDrawer({ editing, onClose }: { editing: Editing; onClose: () => void }) {
  const [form] = Form.useForm<BranchFormValues>();
  const isEdit = editing.mode === 'edit';
  const existing = editing.mode === 'edit' ? editing.branch : undefined;

  const save = useApiMutation<Branch, BranchFormValues>({
    mutationFn: (raw) => {
      const values = blankToNull(raw);

      return isEdit
        ? apiFetch<Branch>(`/api/branch/${existing?.id}`, { method: 'PUT', body: values })
        : apiFetch<Branch>('/api/branch', { method: 'POST', body: values });
    },
    success: isEdit ? 'Branch updated.' : 'Branch created.',
    invalidates: [branchesKey],
    onSuccess: onClose,
  });

  return (
    <FormDrawer<BranchFormValues>
      title={isEdit ? `Edit ${existing?.name ?? 'branch'}` : 'New branch'}
      subtitle={
        isEdit
          ? undefined
          : 'A new branch starts its own document numbering series in each financial year.'
      }
      open
      onClose={onClose}
      form={form}
      initialValues={existing ?? { isHeadOffice: false, isActive: true, code: '', name: '' }}
      onSubmit={(values) => void submitVia(save, values)}
      submitText={isEdit ? 'Save changes' : 'Create branch'}
      submitting={save.isPending}
    >
      <div className="grid grid-cols-2 gap-x-3">
        <Form.Item
          name="code"
          label="Code"
          rules={[{ required: true, message: 'A code is required' }]}
          extra={isEdit ? 'Fixed at creation — document numbers already carry it' : undefined}
        >
          <Input disabled={isEdit} className="font-mono-nums" />
        </Form.Item>

        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, message: 'A name is required' }]}
        >
          <Input />
        </Form.Item>
      </div>

      <Form.Item
        name="gstin"
        label="GSTIN"
        extra="Registration is per state, so it belongs to the branch rather than the company."
        rules={[{ len: 15, message: 'A GSTIN is 15 characters' }]}
      >
        <Input maxLength={15} className="font-mono-nums" />
      </Form.Item>

      <div className="grid grid-cols-2 gap-x-3">
        <Form.Item
          name="stateCode"
          label="State code"
          extra="Two digits, e.g. 24 for Gujarat. Decides CGST/SGST against IGST."
          rules={[{ pattern: /^\d{2}$/, message: 'Two digits' }]}
        >
          <Input maxLength={2} className="font-mono-nums" />
        </Form.Item>

        <Form.Item name="stateName" label="State">
          <Input />
        </Form.Item>
      </div>

      <Form.Item name="addressLine1" label="Address">
        <Input placeholder="Line 1" />
      </Form.Item>

      <Form.Item name="addressLine2" label=" ">
        <Input placeholder="Line 2" />
      </Form.Item>

      <div className="grid grid-cols-2 gap-x-3">
        <Form.Item name="city" label="City">
          <Input />
        </Form.Item>

        <Form.Item
          name="pincode"
          label="PIN"
          rules={[{ pattern: /^\d{6}$/, message: 'Six digits' }]}
        >
          <Input maxLength={6} className="font-mono-nums" />
        </Form.Item>
      </div>

      <div className="grid grid-cols-2 gap-x-3">
        <Form.Item name="contactPerson" label="Contact person">
          <Input />
        </Form.Item>

        <Form.Item name="contactPhone" label="Phone">
          <Input className="font-mono-nums" />
        </Form.Item>
      </div>

      <Form.Item
        name="contactEmail"
        label="Email"
        rules={[{ type: 'email', message: 'Not a valid email address' }]}
      >
        <Input />
      </Form.Item>

      <div className="flex items-center gap-8 border-t border-border-color pt-3">
        <Form.Item
          name="isHeadOffice"
          label="Head office"
          valuePropName="checked"
          extra="Exactly one per company. It gets the consolidated, all-branch view."
        >
          <Switch size="small" />
        </Form.Item>

        {isEdit && (
          <Form.Item
            name="isActive"
            label="Active"
            valuePropName="checked"
            extra="An inactive branch keeps its history but stops appearing for new work."
          >
            <Switch size="small" />
          </Form.Item>
        )}
      </div>
    </FormDrawer>
  );
}
